import angular from 'angular'

import RE from '../utils/re'
import hosth from '../utils/hosth'
import deline from '../utils/deline'

import { toUrl, unescapeHtml } from '../utils/security'
import { toPairs, isPlainObject } from 'lodash-es'
import { isText, isEmpty, defaultTo } from '../utils/bits'
import { addSetToArray, delSetFromArray } from '../utils/arrayh'

const descDiffPlansByPriority = (a, b) =>
  b.priority === a.priority
    ? descDiffPlansByStartIndex(a, b)
    : b.priority - a.priority

const descDiffPlansByStartIndex = (a, b) =>
  b.startIndex - a.startIndex

angular
  .module('ggs.highlighter')
  .factory('highlighter', HighlighterProvider)

function HighlighterProvider($log) {

  const MAIN_CLASS = 'ggs-highlighter'
  const LINK_CLASS = 'ggs-highlighter__link'

  const EMOJI_CLASS = 'ggs-highlighter__emoji'
  const EMOJI_SRC_PLH = ':emoji-src:'
  const EMOJI_ALT_PLH = ':emoji-alt:'
  const EMOJI_IMG_TMPL = `<img alt="${EMOJI_ALT_PLH}" src="${EMOJI_SRC_PLH}" class="${EMOJI_CLASS}">`

  const LINK_FB_CLASS = 'ggs-clr__facebook'
  const LINK_TW_CLASS = 'ggs-clr__twitter'

  const LINKS_PRIORITY = 2
  const NAMES_PRIORITY = 1
  const HASHTAGS_PRIORITY = 0

  function highlighter(
    rawSource,
    postInfo,
    positionToHashtag = null,
    emojiToImg = null,
    userClass = null,
    allowsLinks = true) {

    const source = defaultTo(rawSource, '')
    const linkColorClass = false
      || (postInfo.isFacebookHost && LINK_FB_CLASS)
      || (postInfo.isTwitterHost && LINK_TW_CLASS)

    const linkClass    = joinClasses(LINK_CLASS, linkColorClass)
    const nameClass    = joinClasses(LINK_CLASS, linkColorClass)
    const hashtagClass = joinClasses(LINK_CLASS, linkColorClass)

    const linksRE = RE.LINKS
    const namesRE = hosth.getNamesRE(postInfo)
    const nameToUrl = hosth.getNameToUrl(postInfo)
    const hashtagToUrl = hosth.getTagToUrl(postInfo)

    return format(
      source,
      allowsLinks,
      userClass,
      linkClass,
      nameClass,
      hashtagClass,
      linksRE,
      namesRE,
      positionToHashtag,
      emojiToImg,
      nameToUrl,
      hashtagToUrl)
  }

  highlighter.LINK_FB_CLASS = LINK_FB_CLASS
  highlighter.LINK_TW_CLASS = LINK_TW_CLASS
  return highlighter

  function format(
    source,
    allowsLinks,
    userClass,
    linkClass,
    nameClass,
    hashtagClass,
    linksRE,
    namesRE,
    positionToHashtag,
    emojiToImg,
    nameToUrl,
    hashtagToUrl) {

    let html = source

    const useLinks = allowsLinks && !!linksRE
    const useNames = allowsLinks && !!nameToUrl && !!namesRE
    const useHashtags = allowsLinks && !!hashtagToUrl && !!positionToHashtag

    const plans = []
    if (useLinks) addSetToArray(plans, findLinks(LINKS_PRIORITY, html, linkClass, linksRE))
    if (useNames) addSetToArray(plans, findNames(NAMES_PRIORITY, html, nameClass, namesRE, nameToUrl))
    if (useHashtags) addSetToArray(plans, findHashtags(HASHTAGS_PRIORITY, html, hashtagClass, positionToHashtag, hashtagToUrl))

    const crossPlans = []
    plans.sort(descDiffPlansByPriority)
    for (let outerIndex = 0, length = plans.length; outerIndex !== length; outerIndex++) {
      const outerPlan = plans[outerIndex]
      for (let innerIndex = 0; innerIndex !== outerIndex; innerIndex++) {
        const innerPlan = plans[innerIndex]
        const safe = false
          || innerPlan.startIndex > outerPlan.endIndex
          || innerPlan.endIndex < outerPlan.startIndex
        if (!safe) crossPlans.push(outerPlan)
      }
    }

    delSetFromArray(plans, crossPlans)
    plans.sort(descDiffPlansByStartIndex)
    for (let index = 0, length = plans.length; index !== length; index++) {
      const plan = plans[index]
      const before = html.substring(0, plan.startIndex)
      const after = html.substring(plan.endIndex)
      if ((before + plan.original + after) === html) {
        html = before + plan.replacer + after
      } else {
        $log.warn('highlighter#format: process is interrupted!', source, plans, index, plan, html)
        break
      }
    }

    if (!!emojiToImg) html = improveEmojis(html, EMOJI_IMG_TMPL, EMOJI_SRC_PLH, EMOJI_ALT_PLH, emojiToImg)
    html = improveNewLines(html)
    html = wrapByUserClass(html, userClass, MAIN_CLASS)
    return html
  }

  function findHashtags(priority, _html, hashtagClass, positionToHashtag, hashtagToUrl) {
    const plans = []
    for (let index = 0, length = positionToHashtag.positions.length; index !== length; index++) {
      const position = positionToHashtag.positions[index]
      const hashtag = positionToHashtag.hashtags[index]
      const tag = hashtag.slice(1)
      const href = hashtagToUrl(unescapeHtml(tag))
      if (href) {
        const link = fmtLink(hashtagClass, hashtag, href)
        plans.push({
          priority,
          startIndex: position,
          endIndex: position + hashtag.length,
          original: hashtag,
          replacer: link
        })
      }
    }
    return plans
  }

  function findNames(priority, html, nameClass, namesRE, nameToUrl) {
    const plans = []
    let match
    while ((match = namesRE.exec(html)) !== null) {
      const position = match.index
      const nameWithAt = match[0]
      const name = match[1]
      const href = nameToUrl(unescapeHtml(name))
      if (href) {
        const link = fmtLink(nameClass, nameWithAt, href)
        plans.push({
          priority,
          startIndex: position,
          endIndex: position + nameWithAt.length,
          original: nameWithAt,
          replacer: link
        })
      }
    }
    return plans
  }

  function findLinks(priority, html, linkClass, linksRE) {
    const plans = []
    let match
    while ((match = linksRE.exec(html)) !== null) {
      const position = match.index
      const text = match[0]
      const href = unescapeHtml(match[1])
      if (href) {
        const link = fmtLink(linkClass, text, href)
        plans.push({
          priority,
          startIndex: position,
          endIndex: position + text.length,
          original: text,
          replacer: link
        })
      }
    }
    return plans
  }

  function fmtLink(linkClass, text, rawHref) {
    const href = toUrl(rawHref)
    return deline(`
      <a
        href="${href}"
        tabindex="-1"
        target="_blank" rel="noopener noreferrer"
        class="${linkClass}"
      >${text}</a>`)
  }

  function improveEmojis(html, emojiImgTmpl, emojiSrcPlh, emojiAltPlh, emojiToImg) {
    for (let index = 0, length = emojiToImg.emojis.length; index !== length; index++) {
      const emojiAlt = emojiToImg.emojis[index]
      const emojiSrc = emojiToImg.imgs[index]
      const emojiImg = emojiImgTmpl
        .replace(emojiSrcPlh, emojiSrc)
        .replace(emojiAltPlh, emojiAlt)
      html = html.replace(RegExp(emojiAlt, 'g'), emojiImg)
    }
    return html
  }

  function improveNewLines(html) {
    return html
      .replace(/\r/g, '')
      .replace(/\n{3,}/, '\n\n')
      .replace(/\n/g, '<br>')
  }

  function wrapByUserClass(html, userClass, mainClass) {
    if (isEmpty(userClass)) {
      return `<span class="${mainClass}">${html}</span>`
    } else {
      let strClass = ''
      if (isPlainObject()) {
        for (const [className, classCond]  of toPairs(userClass)) {
          if (classCond) {
            strClass += (' ' + className)
          }
        }
      } else {
        strClass = userClass
      }

      return `<span class="${mainClass}"><span class="${strClass}">${html}</span></span>`
    }
  }

  function joinClasses(...classes) {
    return classes.filter(isText).join(' ')
  }
}
